import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * Branded date picker for the signup flow — replaces the native
 * <input type="date">, whose popup timing/position can't be controlled.
 *
 *  • Opens ONLY when the trigger is tapped (never on focus).
 *  • Mobile (<700px): a bottom sheet (matches the SuSelect sheet) titled with
 *    the field label, so the label is never obscured. Desktop: a popover
 *    anchored just below the field.
 *  • Tap the "Month Year" title to jump years (no native <select>).
 *  • Value in/out is ISO yyyy-mm-dd, so nothing downstream changes.
 */
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const DOW = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"]; // Monday-first (AU)

const pad = (n) => String(n).padStart(2, "0");
const toIso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
function parseIso(v) {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  return m ? { y: +m[1], m: +m[2] - 1, d: +m[3] } : null;
}
const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const firstWeekdayMon = (y, m) => (new Date(y, m, 1).getDay() + 6) % 7;
function formatDisplay(v) {
  const p = parseIso(v);
  return p ? `${pad(p.d)}/${pad(p.m + 1)}/${p.y}` : "";
}
const isMobile = () => typeof window !== "undefined" && window.innerWidth < 700;

export default function SuDatePicker({ value, onChange, label, placeholder = "dd/mm/yyyy", min, max, required, id, name }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("days");   // "days" | "years"
  const [dropPos, setDropPos] = useState(null);
  const btnRef = useRef(null);

  const parsed = parseIso(value);
  const today = new Date();
  const minP = parseIso(min);
  const maxP = parseIso(max);
  const minIso = min || null;
  const maxIso = max || null;

  const [viewY, setViewY] = useState(parsed?.y ?? maxP?.y ?? today.getFullYear());
  const [viewM, setViewM] = useState(parsed?.m ?? today.getMonth());

  // Keep the view in sync if the value changes from outside.
  useEffect(() => {
    if (parsed) { setViewY(parsed.y); setViewM(parsed.m); }
    // eslint-disable-next-line
  }, [value]);

  const openCal = () => {
    if (parsed) { setViewY(parsed.y); setViewM(parsed.m); }
    setMode("days");
    if (!isMobile() && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const up = spaceBelow < 360 && r.top > 360;
      setDropPos({
        top: up ? undefined : r.bottom + 4,
        bottom: up ? (window.innerHeight - r.top + 4) : undefined,
        // Clamp so the fixed 280px popover never spills past the viewport edge
        // (right-column fields like DOB / ID expiry on narrow desktops).
        left: Math.max(8, Math.min(r.left, window.innerWidth - 280 - 8)),
      });
    }
    setOpen(true);
  };

  // Close on outside click; close desktop popover on scroll (the sheet is
  // fixed to the viewport, so leave it alone on scroll).
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (e.target?.closest && (e.target.closest(".su-cal") || e.target.closest(".su-cal-sheet"))) return;
      setOpen(false);
    };
    const onScroll = () => { if (!isMobile()) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    // IMPORTANT: NOT capture. A capture-phase window scroll listener also fires
    // when the user scrolls the year grid INSIDE the desktop popover, which
    // would slam it shut mid-interaction (you couldn't reach a year → couldn't
    // pick a date). Without capture we only catch page/window scroll (which
    // should dismiss the fixed popover); inner scrolls don't bubble, so they're
    // ignored.
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll);
    };
  }, [open]);

  const inRange = (iso) => (!minIso || iso >= minIso) && (!maxIso || iso <= maxIso);
  const pick = (day) => {
    const iso = toIso(viewY, viewM, day);
    if (!inRange(iso)) return;
    onChange(iso);
    setOpen(false);
  };
  const prevMonth = () => { if (viewM === 0) { setViewY((y) => y - 1); setViewM(11); } else setViewM((m) => m - 1); };
  const nextMonth = () => { if (viewM === 11) { setViewY((y) => y + 1); setViewM(0); } else setViewM((m) => m + 1); };
  const monthDisabled = (y, m) => {
    const first = toIso(y, m, 1);
    const last = toIso(y, m, daysInMonth(y, m));
    return (maxIso && first > maxIso) || (minIso && last < minIso);
  };

  const dim = daysInMonth(viewY, viewM);
  const lead = firstWeekdayMon(viewY, viewM);

  const minYear = minP?.y ?? 1900;
  const maxYear = maxP?.y ?? (today.getFullYear() + 10);
  const years = [];
  for (let y = maxYear; y >= minYear; y--) years.push(y);

  const chevron = <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>;

  const calendar = (
    <div className="su-cal-inner">
      <div className="su-cal-header">
        {mode === "days"
          ? <button type="button" className="su-cal-nav" onMouseDown={(e) => { e.preventDefault(); prevMonth(); }} aria-label="Previous month"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg></button>
          : <span className="su-cal-nav-spacer" />}
        <div className="su-cal-hdr-selects">
          <button type="button" className={`su-cal-sel${mode === "months" ? " active" : ""}`} onMouseDown={(e) => { e.preventDefault(); setMode(mode === "months" ? "days" : "months"); }}>
            {MONTHS[viewM]}{chevron}
          </button>
          <button type="button" className={`su-cal-sel${mode === "years" ? " active" : ""}`} onMouseDown={(e) => { e.preventDefault(); setMode(mode === "years" ? "days" : "years"); }}>
            {viewY}{chevron}
          </button>
        </div>
        {mode === "days"
          ? <button type="button" className="su-cal-nav" onMouseDown={(e) => { e.preventDefault(); nextMonth(); }} aria-label="Next month"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg></button>
          : <span className="su-cal-nav-spacer" />}
      </div>

      {mode === "days" && (
        <div className="su-cal-grid">
          {DOW.map((d) => <div key={d} className="su-cal-dow">{d}</div>)}
          {Array.from({ length: lead }, (_, i) => <div key={`g${i}`} />)}
          {Array.from({ length: dim }, (_, i) => {
            const day = i + 1;
            const sel = parsed && parsed.y === viewY && parsed.m === viewM && parsed.d === day;
            const dis = !inRange(toIso(viewY, viewM, day));
            return (
              <button key={day} type="button" disabled={dis}
                className={`su-cal-day${sel ? " su-cal-day-sel" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); if (!dis) pick(day); }}>{day}</button>
            );
          })}
        </div>
      )}

      {mode === "months" && (
        <div className="su-cal-months">
          {MONTHS.map((mn, mi) => {
            const dis = monthDisabled(viewY, mi);
            return (
              <button key={mn} type="button" disabled={dis}
                className={`su-cal-month${mi === viewM ? " sel" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); if (!dis) { setViewM(mi); setMode("days"); } }}>{mn.slice(0, 3)}</button>
            );
          })}
        </div>
      )}

      {mode === "years" && (
        <div className="su-cal-years">
          {years.map((y) => (
            <button key={y} type="button"
              className={`su-cal-year${y === viewY ? " sel" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); setViewY(y); setMode("days"); }}>{y}</button>
          ))}
        </div>
      )}

      {value && (
        <div className="su-cal-footer">
          <button type="button" className="su-cal-clear" onMouseDown={(e) => { e.preventDefault(); onChange(""); setOpen(false); }}>Clear</button>
        </div>
      )}
    </div>
  );

  return (
    <div className={`su-datepicker${open ? " open" : ""}`}>
      <button
        ref={btnRef}
        type="button"
        id={id}
        className={`su-dp-trigger${!parsed ? " su-dp-empty" : ""}`}
        onClick={() => (open ? setOpen(false) : openCal())}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label ? `${label}${formatDisplay(value) ? `: ${formatDisplay(value)}` : ""}` : undefined}
      >
        <span>{formatDisplay(value) || placeholder}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>

      {/* Hidden field carries `required` validation for the wrapping <form>. */}
      {required && (
        <input tabIndex={-1} required readOnly value={value || ""} name={name}
          style={{ opacity: 0, width: 0, height: 0, position: "absolute", pointerEvents: "none" }} />
      )}

      {open && (isMobile()
        ? createPortal(
          <div className="su-sheet-backdrop" onMouseDown={() => setOpen(false)}>
            <div className="su-cal-sheet su-sheet" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
              <div className="su-sheet-handle" />
              <div className="su-sheet-header">
                <span className="su-sheet-title">{label || "Select date"}</span>
                <button type="button" className="su-sheet-close" onClick={() => setOpen(false)} aria-label="Close">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
              {calendar}
            </div>
          </div>,
          document.body)
        : (dropPos && createPortal(
          <div className="su-cal"
            style={{ position: "fixed", top: dropPos.top ?? "auto", bottom: dropPos.bottom ?? "auto", left: dropPos.left, zIndex: 9999 }}
            onMouseDown={(e) => e.stopPropagation()}>
            {calendar}
          </div>,
          document.body))
      )}
    </div>
  );
}
